import type { StreamEntry } from "./history/types.js";
import { isCutOf } from "./state.svelte.js";
import { finishedTaskIdOf } from "./task-notification-prompt.js";

type Row = Record<string, unknown>;

function itemIdentities(raw: Row): string[] {
  if (raw.type === "tool_call") {
    if (typeof raw.callId !== "string") return [];
    // A task's two finished-line frames are one line (toolCardIndexOf).
    const taskId = finishedTaskIdOf({ name: String(raw.name), metadata: raw.metadata });
    return [`call:${raw.callId}`, ...(taskId ? [`task:${taskId}`] : [])];
  }
  const ids: string[] = [];
  if (typeof raw.messageId === "string" && raw.messageId) ids.push(`msg:${raw.messageId}`);
  if (raw.type === "user_message" && typeof raw.clientMessageId === "string") {
    ids.push(`cmid:${raw.clientMessageId}`);
  }
  return ids;
}

function entryIdentities(entry: StreamEntry): string[] {
  if (entry.kind === "tool_call") {
    return [`call:${entry.callId}`, ...(entry.taskId ? [`task:${entry.taskId}`] : [])];
  }
  if (entry.kind !== "text" && entry.kind !== "user_message") return [];
  const ids = [entry.messageId, ...(entry.aliasIds ?? [])]
    .filter((id): id is string => typeof id === "string")
    .map((id) => `msg:${id}`);
  if (entry.kind === "user_message" && entry.clientMessageId)
    ids.push(`cmid:${entry.clientMessageId}`);
  return ids;
}

const shares = (entry: StreamEntry, ids: readonly string[]): boolean =>
  entryIdentities(entry).some((id) => ids.includes(id));

// The screen block a page's text starts in: the one its text run is the tail of, else the message's
// last block (a reply split text / tool / text keeps its earlier blocks above).
function textAnchor(
  entries: readonly StreamEntry[],
  items: readonly Row[],
  k: number,
  ids: string[],
): number {
  let run = "";
  for (let i = k; i < items.length && items[i].messageId === items[k].messageId; i += 1) {
    if (items[i].type !== "assistant_message") break;
    run += typeof items[i].text === "string" ? items[i].text : "";
  }
  const blocks = entries.flatMap((e, i) => (e.kind === "text" && shares(e, ids) ? [i] : []));
  const cut = blocks.find((i) => {
    const content = (entries[i] as { content: string }).content;
    return content === run || isCutOf(run, content);
  });
  return cut ?? blocks[blocks.length - 1];
}

// Where a page starts on screen: its first row that names a screen entry. undefined: none does.
function pageStart(
  entries: readonly StreamEntry[],
  items: readonly Row[],
): { at: number; k: number } | undefined {
  for (let k = 0; k < items.length; k += 1) {
    const ids = itemIdentities(items[k]);
    if (ids.length === 0) continue;
    if (
      items[k].type === "assistant_message" &&
      entries.some((e) => e.kind === "text" && shares(e, ids))
    ) {
      return { at: textAnchor(entries, items, k, ids), k };
    }
    const at = entries.findIndex((e) => e.kind !== "text" && shares(e, ids));
    if (at >= 0) return { at, k };
  }
  return undefined;
}

// A seq-less page (session pages, knowledge/126) has no floor: the screen above where it starts
// stays, in order, so a refresh never shrinks the body (#4401). No anchor: the page replaces, since
// keeping the screen would bridge a hole the page does not cover.
export function entriesAboveSeqlessPage(
  entries: readonly StreamEntry[],
  items: readonly Row[],
): readonly StreamEntry[] {
  if (items.length === 0 || items.some((raw) => typeof raw.seq === "number")) return [];
  const start = pageStart(entries, items);
  if (!start) return [];
  let end = start.at;
  // The page's leading id-less rows (reasoning) redraw the id-less run right above its start.
  if (start.k > 0) {
    while (
      end > 0 &&
      entryIdentities(entries[end - 1]).length === 0 &&
      entries[end - 1].kind !== "turn_boundary"
    ) {
      end -= 1;
    }
  }
  return entries.slice(0, end);
}

// Never across a rewind, and never an optimistic prompt (mergeOptimisticRows puts those back).
export function seqlessHeldAbove(
  entries: readonly StreamEntry[],
  items: readonly Row[],
  mode: "refresh" | "rewind" | undefined,
  optimistic: readonly StreamEntry[],
): readonly StreamEntry[] {
  if (mode === "rewind") return [];
  return entriesAboveSeqlessPage(entries, items).filter((e) => !optimistic.includes(e));
}

// The kept rows go above the page's. A row both carry (a tool call completing inside the page) keeps
// its place on screen and takes the page's newer state.
export function joinAbove(
  held: readonly StreamEntry[],
  page: readonly StreamEntry[],
): StreamEntry[] {
  let rest = [...page];
  const above = held.map((entry) => {
    const ids = entryIdentities(entry);
    if (entry.kind === "text" || ids.length === 0) return entry;
    const twin = rest.find((p) => p.kind === entry.kind && shares(p, ids));
    if (!twin) return entry;
    rest = rest.filter((p) => p !== twin);
    return twin;
  });
  return [...above, ...rest];
}
